/**
 * ⟦SPEC-PRODUCTEUR §5.5 / §11.4 · A-81⟧ **La mise en mots d'une délégation** — ce que l'écran
 * Équipe dit d'un jour délégué, d'une cause d'argent, d'une vidéo produite.
 *
 * Ce fichier ne porte que du vocabulaire et du choix de gabarit. **Aucune règle, aucun
 * recalcul** : les chiffres viennent de `RecapDelegue`, mesurés par le moteur (A-16 : la cause,
 * jamais le solde), et les phrases viennent du contenu (`producteur.json`, `journal`).
 */

import type { JourDelegue, RecapCause, VideoClass } from "@maxime/shared";

import { nombre } from "./format";

/**
 * ⟦§11.4⟧ La ligne quotidienne, tirée des gabarits du contenu — **jour modulo longueur**, aucun
 * tirage : la même journée se relit avec la même phrase, et douze gabarits suffisent pour
 * qu'un mois délégué ne bégaie pas. Les trous sont remplis par les compteurs du jour ; un
 * gabarit sans trou est une phrase d'ambiance, et c'est permis.
 *
 * `null` quand le contenu ne porte pas de gabarits : l'écran garde sa ligne « Jour N… » d'avant
 * — l'état neutre ⟦A-10⟧.
 */
export function ligneQuotidienne(gabarits: readonly string[] | undefined, jour: JourDelegue): string | null {
  if (!gabarits || gabarits.length === 0) return null;
  const gabarit = gabarits[((jour.jour % gabarits.length) + gabarits.length) % gabarits.length]!;
  const valeurs: Record<string, string> = {
    jour: nombre(jour.jour),
    abonnes: nombre(jour.abonnes),
    argent: nombre(jour.argent),
    energie: nombre(jour.energie),
    sm: nombre(jour.sm),
    videos: nombre(jour.videos),
  };
  return gabarit.replace(/\{(jour|abonnes|argent|energie|sm|videos)\}/g, (_, cle: string) => valeurs[cle] ?? "");
}

/**
 * ⟦§5.5⟧ Les causes du solde d'argent, dans les mots de la table : « les courbes de tes vidéos »,
 * « la rente de tes vidéos intemporelles », « les charges », « les salaires ». Tout le reste du vocabulaire ⟦A-16⟧
 * a aussi sa phrase — une cause inconnue ne compilerait pas.
 */
export function libelleCauseArgent(cause: RecapCause): string {
  switch (cause) {
    case "vues_versees":
      return "les courbes de tes vidéos";
    case "rente_evergreen":
      return "la rente de tes vidéos intemporelles";
    case "charges":
      return "les charges";
    case "salaires":
      return "les salaires";
    case "cout_action":
      return "le travail (freelances, production)";
    case "effet_action":
      return "les primes et les achats";
    case "evenement":
      return "ce qui t'est tombé dessus";
    case "progression":
      return "ta progression";
    case "publication":
      return "les mises en ligne";
    case "nuit":
      return "la nuit";
    case "crunch":
    case "depassement":
    case "nuits_contenues":
    case "idee_perimee":
    case "erosion_chaos":
    case "rep_lumiere":
    case "desabonnements":
    case "momentum_silence":
    case "opportunite_expiree":
    case "tendance":
    case "rival":
    case "fil":
      return "le reste";
  }
}

/** La classe d'une vidéo, en un mot de l'univers. */
export function libelleClasse(classe: VideoClass): string {
  switch (classe) {
    case "trend":
      return "tendance";
    case "standard":
      return "standard";
    case "evergreen":
      return "intemporelle";
    case "nulle":
      return "nulle";
  }
}
